// Pure geometry for a booking bar on a day-column grid. No DOM. Both views
// consume this, which is the only reason the timeline and the month grid
// agree about where a booking sits.
//
// A bar covers WHOLE CELLS: from the left edge of its pickup day to the right
// edge of its return day. A booking visibly occupies the days it occupies.
//
// Both ends are then slanted by half a column, so every bar is a parallelogram
// — always, whether or not it has a neighbour. Where one rental hands over to
// the next on the same vehicle, the outgoing bar is shortened so the two TILE
// the shared day rather than overlapping it (see "the shared-day rule" below).
// A genuine double-booking is a different thing and still stacks into lanes —
// that distinction is markTurnarounds' job, not this one's.

const MS_PER_DAY = 86400000;

// Every dimension here is a multiple of colWidth, never a pixel. The week view
// passes pixels and the month grid passes percentage points (100/7), and the
// same numbers have to be right in both — a literal `3` would mean 3px in one
// and a fifth of a column in the other.
export const SLANT_RATIO = 0.5; // the slant spans half a day column
const SEAM_RATIO = 0.05; // the gap between two bars: ~3.2px at a 64px column
const MIN_BODY_RATIO = 0.375; // ~24px — the floor that keeps a bar visible

function dateOnly(isoString) {
  return String(isoString).split("T")[0];
}

// Whole-day difference between two YYYY-MM-DD strings. Built from UTC so DST
// transitions can never make a day 23 or 25 hours long and shift a column.
function dayIndex(fromDateString, toDateString) {
  const [fy, fm, fd] = fromDateString.split("-").map(Number);
  const [ty, tm, td] = toDateString.split("-").map(Number);
  const from = Date.UTC(fy, fm - 1, fd);
  const to = Date.UTC(ty, tm - 1, td);
  return Math.round((to - from) / MS_PER_DAY);
}

/**
 * @param {{start: string, end: string}} event  ISO-ish local datetimes
 * @param {{startDate: string, dayCount: number}} range
 * @param {number} colWidth  one day column, in whatever unit the caller works
 *   in — the week view passes pixels, the month view passes `100 / 7` so the
 *   results come back as percentages of a fluid row.
 * @param {{diagonalStart: boolean, diagonalEnd: boolean}} [turnaround]  from
 *   markTurnarounds. `diagonalEnd` means the next booking on this vehicle
 *   collects on this bar's return day.
 * @param {{maxCut?: number}} [options]  `maxCut` caps the slant, in the same
 *   unit as colWidth. Use it where a column is wide relative to the bar's
 *   height — the month grid — to keep the diagonal from flattening out.
 * @returns {{left: number, width: number, continuesLeft: boolean,
 *   continuesRight: boolean, cutLeft: number, cutRight: number}|null}
 *   cutLeft/cutRight are FRACTIONS of width (0–1) rather than lengths, so the
 *   CSS can consume them as percentages of the bar's own box. That matters:
 *   nothing repaints on window resize, so a ratio is the only thing that stays
 *   correct afterwards.
 */
export function barGeometry(event, range, colWidth, turnaround, options = {}) {
  const startIdx = dayIndex(range.startDate, dateOnly(event.start));
  const endIdx = dayIndex(range.startDate, dateOnly(event.end));

  // Entirely before or entirely after the visible range.
  if (endIdx < 0 || startIdx > range.dayCount - 1) return null;

  // The slant's ANGLE is atan(barHeight / slant), so on a very wide column half
  // a column is a nearly flat smear rather than a corner — the month grid's
  // columns are ~4x the week view's against a shorter bar. `maxCut` caps it.
  // It must be applied HERE and not in CSS: the shortening below assumes the
  // cut is exactly `slant`, so a cut capped anywhere else would leave the two
  // bars of a handover overlapping instead of tiling.
  const { maxCut = Infinity } = options;
  const slant = Math.min(colWidth * SLANT_RATIO, maxCut);
  const seam = colWidth * SEAM_RATIO;
  const containerWidth = range.dayCount * colWidth;

  const rawLeft = startIdx * colWidth;
  let rawRight = (endIdx + 1) * colWidth;

  // The shared-day rule. On a handover, pull this bar's right edge back so its
  // bottom-right corner lands on the next booking's bottom-left and its
  // top-right on that bar's top-left: the two then tile the day they share.
  if (turnaround && turnaround.diagonalEnd) {
    const tiled = endIdx * colWidth + slant;
    // A one-day booking that also hands over would shorten to exactly `slant`,
    // whose top and bottom edges are both zero-length — an invisible bar. Floor
    // it and accept a small overlap: an overlap you can see beats a booking
    // that silently disappears.
    //
    // Measured from the CLIPPED left edge, not `rawLeft`. A booking that starts
    // before the visible range has a negative rawLeft, which makes the floor
    // negative too — it never binds, while the cut below is divided by the
    // clipped width. In month view every week row re-clips the bar, so that is
    // the ordinary case, not an exotic one.
    rawRight = Math.max(tiled, Math.max(rawLeft, 0) + slant + colWidth * MIN_BODY_RATIO);
  }

  const continuesLeft = rawLeft < 0;
  const continuesRight = rawRight > containerWidth;

  const left = Math.max(rawLeft, 0);
  const right = Math.min(rawRight, containerWidth);

  // An off-board end is a viewport cut, not an ending: it gets neither the seam
  // inset nor the slant, and keeps its dashed continuation hint instead.
  const boxLeft = left + (continuesLeft ? 0 : seam / 2);
  const width = right - (continuesRight ? 0 : seam / 2) - boxLeft;

  // Inverted input (end before start) would otherwise divide by a negative
  // width below and yield negative cut fractions — a polygon with negative
  // percentages, which renders as nothing with no error anywhere. Treat it the
  // same as out-of-range. A handover can never reach here: the shortening is
  // floored at rawLeft + slant + MIN_BODY_RATIO * colWidth.
  if (width <= 0) return null;

  // No minimum-width floor is needed beyond the handover case above: a visible
  // bar always covers at least one whole column.
  //
  // Each cut is capped at the bar's own width. Past 100% that edge inverts and
  // the clip-path paints nothing — the bar disappears with no error in the
  // console and nothing to see on the board. The floor above is what should
  // normally keep them in range; this is the guarantee, and it holds for
  // callers working in pixels and in percentage points alike.
  //
  // Capped INDEPENDENTLY, not as a sum: cutLeft + cutRight legitimately exceeds
  // the width on a one-day bar, whose two slants cross to leave the narrow
  // sliver the design asks for. Only a single cut wider than the box is
  // degenerate.
  const cutL = continuesLeft ? 0 : Math.min(slant, width);
  const cutR = continuesRight ? 0 : Math.min(slant, width);

  return {
    left: boxLeft,
    width,
    continuesLeft,
    continuesRight,
    cutLeft: cutL / width,
    cutRight: cutR / width
  };
}
